// SalesInterpretation.tsx
import React, { useMemo } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import { analyzePastEventsPattern, projectEventSales } from '../services/salesProjectionService';

interface TicketType {
  name: string;
  quantity: number;
  quantity_sold: number;
  price: number;
  revenue: number;
}

interface Registration {
  completed_at: string;
  tickets_count: number;
}

interface TitoEvent {
  title: string;
  start_date: string;
  totalSold: number;
  totalAvailable: number;
  totalRevenue: number;
  ticketTypes: TicketType[];
  registrations: Registration[];
  venueCapacity?: number;
}

interface PastEvent {
  id: string;
  title: string;
  start_date: string;
  totalSold: number;
  registrations: Registration[];
}

interface SalesInterpretationProps {
  event: TitoEvent;
  pastEvents?: PastEvent[];
}

const SalesInterpretation: React.FC<SalesInterpretationProps> = ({ event, pastEvents = [] }) => {
  const { totalSold, totalAvailable, ticketTypes, registrations, start_date, venueCapacity } = event;

  const interpretation = useMemo(() => {
    const messages: string[] = [];
    const capacity = venueCapacity || totalAvailable;
    const currentCapacityPercent = capacity > 0 ? Math.round((totalSold / capacity) * 100) : 0;

    // Calculate days until event and days on sale
    const eventDate = new Date(start_date);
    const now = new Date();
    const daysUntilEvent = Math.ceil((eventDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    let daysOnSale = 0;
    let weeksOnSale = 0;
    if (registrations.length > 0) {
      const earliestReg = new Date(Math.min(...registrations.map(r => new Date(r.completed_at).getTime())));
      daysOnSale = Math.floor((now.getTime() - earliestReg.getTime()) / (1000 * 60 * 60 * 24));
      weeksOnSale = Math.floor(daysOnSale / 7);
    }

    // Check for recent sales activity
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    const recentSales = registrations.filter(reg => new Date(reg.completed_at) >= oneWeekAgo);
    const hasRecentActivity = recentSales.length > 0;

    // Get projection if we have enough past data
    let projection = null;
    let projectedCapacityPercent = 0;
    if (pastEvents.length >= 3 && registrations.length > 0) {
      try {
        const baselinePattern = analyzePastEventsPattern(pastEvents);
        if (baselinePattern.length > 0) {
          projection = projectEventSales(
            {
              start_date,
              totalSold,
              registrations,
              venueCapacity,
              totalAvailable
            },
            baselinePattern
          );
          projectedCapacityPercent = Math.round(projection.projectedCapacityPercentage);
        }
      } catch (error) {
        console.error('Projection error:', error);
      }
    }

    // === OVERALL STATUS ===
    
    // Sold out
    if (currentCapacityPercent >= 92) {
      messages.push(`Event is sold out at ${currentCapacityPercent}% capacity (${totalSold}/${capacity} tickets). ${daysUntilEvent > 0 ? `Sold out ${daysUntilEvent} days in advance.` : ''} Consider adding a second show or starting a waitlist.`);
      return messages; // No further analysis needed for sold out events
    }

    // Status summary with projection
    if (projection && projection.confidence !== 'low') {
      messages.push(`Current status: ${totalSold}/${capacity} tickets sold (${currentCapacityPercent}%). Projected final: ${projection.projectedFinalSales} tickets (${projectedCapacityPercent}% capacity). ${daysUntilEvent} days until event, ${weeksOnSale} weeks on sale.`);
    } else {
      messages.push(`Current status: ${totalSold}/${capacity} tickets sold (${currentCapacityPercent}%). ${daysUntilEvent} days until event, ${weeksOnSale > 0 ? `${weeksOnSale} weeks on sale` : 'recently launched'}.`);
    }

    // === PERFORMANCE ASSESSMENT ===
    
    // Excellent performance
    if (currentCapacityPercent >= 70) {
      messages.push(`Sales are strong. On track for a successful event.`);
    }
    // Good but needs push
    else if (currentCapacityPercent >= 50) {
      if (daysUntilEvent < 14) {
        messages.push(`Sales are decent but event is approaching. Final push recommended to maximize attendance.`);
      } else {
        messages.push(`Sales are progressing steadily. Continue current marketing efforts.`);
      }
    }
    // Underperforming
    else if (currentCapacityPercent >= 30) {
      if (daysUntilEvent < 21) {
        messages.push(`Sales are below target with ${daysUntilEvent} days remaining. Immediate marketing intervention required.`);
      } else {
        messages.push(`Sales are below expectations. Consider promotional campaign or ticket offers.`);
      }
    }
    // Critical
    else if (currentCapacityPercent < 30) {
      if (daysUntilEvent < 14) {
        messages.push(`Sales are critically low with only ${daysUntilEvent} days remaining. Urgent action required.`);
      } else {
        messages.push(`Sales are significantly below target. Reassess marketing strategy and pricing.`);
      }
    }

    // === PROJECTION ANALYSIS ===
    if (projection && projection.confidence !== 'low') {
      if (projectedCapacityPercent >= 80) {
        // Do nothing - already covered in performance assessment
      } else if (projectedCapacityPercent >= 60) {
        messages.push(`Based on historical patterns, projected to reach ${projectedCapacityPercent}% capacity. Additional marketing push could improve this outcome.`);
      } else if (projectedCapacityPercent >= 40) {
        messages.push(`Current trajectory projects ${projectedCapacityPercent}% capacity. Below viable threshold - intervention needed to improve projection.`);
      } else {
        messages.push(`Projection indicates ${projectedCapacityPercent}% final capacity. Event viability at risk unless sales pattern changes significantly.`);
      }
    }

    // === SALES MOMENTUM ===
    if (!hasRecentActivity && totalSold > 0 && daysOnSale > 7) {
      const weeksUntilEvent = Math.ceil(daysUntilEvent / 7);
      if (weeksUntilEvent > 2) {
        messages.push(`No sales in the past 7 days. Currently in mid-sale period - typical slowdown but monitor closely. Consider time-limited promotion to maintain momentum.`);
      } else {
        messages.push(`No sales in the past 7 days with only ${daysUntilEvent} days remaining. Red flag - immediate action required.`);
      }
    }

    // === TICKET TYPE ANALYSIS ===
    // Filter out Guest tickets and analyze only public ticket types
    const publicTicketTypes = ticketTypes.filter(tt => 
      !tt.name.toLowerCase().includes('guest') && 
      !tt.name.toLowerCase().includes('comp')
    );

    publicTicketTypes.forEach(tt => {
      const ticketsLeft = tt.quantity - tt.quantity_sold;
      const typeRatio = tt.quantity > 0 ? tt.quantity_sold / tt.quantity : 0;

      // Fully sold out type
      if (tt.quantity > 0 && tt.quantity_sold === tt.quantity) {
        messages.push(`"${tt.name}" tier is sold out.`);
      }
      // Nearly sold out
      else if (typeRatio >= 0.8 && ticketsLeft <= 10 && ticketsLeft > 0) {
        messages.push(`Only ${ticketsLeft} "${tt.name}" tickets remaining.`);
      }
      // Not moving
      else if (typeRatio === 0 && daysOnSale > 14) {
        messages.push(`"${tt.name}" tier has not sold any tickets after ${weeksOnSale} weeks. Review pricing or promotional strategy for this tier.`);
      }
      // Moving slowly
      else if (typeRatio < 0.2 && daysOnSale > 21 && tt.quantity > 10) {
        messages.push(`"${tt.name}" tier moving slowly - ${tt.quantity_sold}/${tt.quantity} sold. Consider targeted promotion.`);
      }
    });

    return messages;
  }, [totalSold, totalAvailable, ticketTypes, registrations, start_date, venueCapacity, pastEvents]);

  return (
    <Box sx={{ mt: 3, p: 2, border: '1px solid #ccc', borderRadius: 1 }}>
      <Typography variant="h6" gutterBottom>Sales Interpretation</Typography>
      {interpretation.length === 0 ? (
        <Typography>No sales data available yet.</Typography>
      ) : (
        interpretation.map((msg, idx) => (
          <Typography key={idx} variant="body2" sx={{ mb: 1 }}>
            • {msg}
          </Typography>
        ))
      )}
    </Box>
  );
};

export default SalesInterpretation;